package org.gstechnovos.jexonplastech.util

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.w3c.dom.Element

/**
 * Reusable Compose hook that uses the native browser IntersectionObserver
 * to detect when an element scrolls into the viewport.
 */
@Composable
fun rememberScrollVisibility(threshold: Double = 0.15): Pair<Boolean, (Element) -> Unit> {
    var isVisible by remember { mutableStateOf(false) }

    val ref: (Element) -> Unit = remember {
        { element ->
            // Use our native external bindings defined below
            val observer = IntersectionObserver(
                callback = { entries, obs ->
                    if (entries.any { it.isIntersecting }) {
                        isVisible = true
                        obs.disconnect() // Stop observing once it animates in
                    }
                },
                options = js("{ threshold: threshold }")
            )

            observer.observe(element)
        }
    }

    return Pair(isVisible, ref)
}

// --- Animation Modifiers ---

fun Modifier.animateSlideUp(isVisible: Boolean, delayMs: Int = 0): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .translateY(if (isVisible) 0.px else 40.px)
    .transition(
        Transition.of("opacity", duration = 0.8.s, delay = (delayMs / 1000.0).s),
        Transition.of("transform", duration = 0.8.s, delay = (delayMs / 1000.0).s)
    )

fun Modifier.animateSlideInLeft(isVisible: Boolean, delayMs: Int = 0): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .translateX(if (isVisible) 0.px else (-40).px)
    .transition(
        Transition.of("opacity", duration = 0.8.s, delay = (delayMs / 1000.0).s),
        Transition.of("transform", duration = 0.8.s, delay = (delayMs / 1000.0).s)
    )

fun Modifier.animateSlideInRight(isVisible: Boolean, delayMs: Int = 0): Modifier = this
    .opacity(if (isVisible) 1 else 0)
    .translateX(if (isVisible) 0.px else 40.px)
    .transition(
        Transition.of("opacity", duration = 0.8.s, delay = (delayMs / 1000.0).s),
        Transition.of("transform", duration = 0.8.s, delay = (delayMs / 1000.0).s)
    )

// --- Native Browser API Bindings ---
// This bypasses the Kobweb wrapper bug and binds directly to the JS engine.

external interface IntersectionObserverEntry {
    val isIntersecting: Boolean
}

external class IntersectionObserver(
    callback: (Array<IntersectionObserverEntry>, IntersectionObserver) -> Unit,
    options: dynamic = definedExternally
) {
    fun observe(element: Element)
    fun disconnect()
}